import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Button,
  DetailList,
  Heading,
  Loading,
} from '@orobix/orobix-fe-components'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import TrainingInfo from '../../components/TrainingInfo/TrainingInfo'
import { useStore } from '../../store/store'
import { ObservationCompact } from '../../types/observation'
import { get } from '../../utils/api'
import { handleApiErrors } from '../../utils/apiErrors'
import { formatDate } from '../../utils/date'
import { locations } from '../../utils/locations'
import useStyles from './DatasetDetails.styles'
import { Dataset } from './DatasetsList'
import DatasetsNotes from './DatasetsNotes'
import DatasetsNotesUpdate from './DatasetsNotesUpdate'
import DatasetTestsList from './DatasetTestsList'
import DatasetTrainingsList from './DatasetTrainingsList'

interface DatasetDetailsProps {
  datasetId: number
  onNotesUpdated?: (notes: string) => void
}

interface RouteParams {
  productId: string
  projectId: string
}

const DatasetDetails = ({ datasetId, onNotesUpdated }: DatasetDetailsProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const history = useHistory()
  const { productId, projectId } = useParams<RouteParams>()
  const { product } = useStore()
  const [dataset, setDataset] = useState<Dataset | undefined>()
  const [notes, setNotes] = useState<string>('')
  const [notesUpdateModal, setNotesUpdateModal] = useState(false)
  const [observations, setObservations] = useState<ObservationCompact[]>([])

  useEffect(() => {
    get<Dataset>(`/dataset/${datasetId}/`)
      .then((data) => {
        setDataset(data)
        setNotes(data.notes || '')
      })
      .catch((errors: AxiosError) => {
        handleApiErrors(errors)
      })
  }, [datasetId])

  useEffect(() => {
    get<{ results: ObservationCompact[]; count: number }>(
      `/observations/?product=${productId}&dataset=${datasetId}&limit=20000&mode=no_image_data`
    )
      .then((data) => {
        setObservations(data.results || [])
      })
      .catch((errors: AxiosError) => {
        handleApiErrors(errors)
      })
  }, [datasetId, productId])

  if (!dataset) {
    return <Loading />
  }

  return (
    <>
      <div className={classes.datasetDetails}>
        <div className={classes.datasetHead}>
          <div className={classes.datasetName}>
            <Heading headingLevel="2" noTopMargin noBottomMargin>
              {dataset.name}
            </Heading>
          </div>

          <div className={classes.buttons}>
            <Button
              onClick={() =>
                history.push(
                  locations.datasetEdit(
                    Number(projectId),
                    Number(productId),
                    datasetId
                  )
                )
              }
              label={t('Details')}
              buttonType="button"
              type="action"
              size="sm"
            />

            <Button
              onClick={() =>
                history.push({
                  hash: '',
                  state: { datasetId: undefined },
                })
              }
              label={t('Close')}
              hideLabel
              icon={<FontAwesomeIcon icon={faTimes} />}
              type="default"
              buttonType="button"
              size="sm"
            />
          </div>
        </div>

        <DetailList
          list={[
            {
              title: t('Created on'),
              value: formatDate((dataset.creationDate as unknown) as Date),
            },
            {
              title: t('Updated on'),
              value: formatDate((dataset.updateDate as unknown) as Date),
            },
          ]}
        />

        {product?.type && (
          <TrainingInfo
            selectedObservations={observations}
            productType={product.type}
          />
        )}

        <DatasetsNotes
          notes={notes}
          onClick={() => setNotesUpdateModal(true)}
        />
      </div>

      <div>
        <Heading headingLevel="3" noTopMargin>
          {t('Trainings')}
        </Heading>
        <DatasetTrainingsList key={datasetId} datasetId={datasetId} />
      </div>

      <div>
        <Heading headingLevel="3" noTopMargin>
          {t('Tests')}
        </Heading>
        <DatasetTestsList key={datasetId} datasetId={datasetId} />
      </div>

      {notesUpdateModal && (
        <DatasetsNotesUpdate
          onClose={() => setNotesUpdateModal(false)}
          onSuccess={() => {
            toast.success(t('The dataset has been edited'))
            setNotesUpdateModal(false)
          }}
          datasetId={dataset.id}
          initialValues={{ notes }}
          setNotes={(newNotes) => {
            setNotes(newNotes)
            onNotesUpdated?.(newNotes as string)
          }}
        />
      )}
    </>
  )
}

export default DatasetDetails
